import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Edit, Trash2, Eye, EyeOff, History } from 'lucide-react'
import { useMembers, useDeleteMember, useUpdateMember } from '@/hooks/use-members'
import { usePlanLimit } from '@/hooks/use-plan-limit'
import type { Tables } from '@/types/supabase'
import { FeatureGate } from '@/components/feature-gate'

type IdolMember = Tables<'idol_members'>

export default function MembersPage() {
  const navigate = useNavigate()
  const { data: members = [], isLoading } = useMembers()
  const deleteMember = useDeleteMember()
  const updateMember = useUpdateMember()
  const { limit: memberLimit, isUnlimited } = usePlanLimit('max_members')

  const [deleteTarget, setDeleteTarget] = useState<IdolMember | null>(null)

  const atLimit = !isUnlimited && memberLimit !== null && members.length >= memberLimit

  if (isLoading) return <p className="text-muted-foreground">Memuat member...</p>

  return (
    <FeatureGate feature="members">
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Member</h1>
          <p className="text-muted-foreground">Kelola daftar anggota idol group Anda</p>
          {!isUnlimited && memberLimit !== null && (
            <p className="mt-1 text-xs text-muted-foreground">
              {members.length}/{memberLimit} member
            </p>
          )}
        </div>
        <div className="flex flex-col items-end gap-1">
          <button
            onClick={() => !atLimit && navigate('/members/new')}
            disabled={atLimit}
            title={atLimit ? `Batas ${memberLimit} member tercapai. Upgrade plan untuk menambah lebih.` : undefined}
            className="flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Plus className="h-4 w-4" />
            Tambah Member
          </button>
          {atLimit && (
            <p className="text-xs text-destructive">
              Batas plan tercapai.{' '}
              <a href="/settings/subscription" className="underline hover:text-destructive/80">
                Upgrade plan
              </a>
            </p>
          )}
        </div>
      </div>

      {members.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border py-16 text-center text-muted-foreground">
          Belum ada member. Tambahkan member pertama Anda.
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
          {members.map((member) => {
            const displayName = member.stage_name ?? member.name
            const initial = displayName.charAt(0).toUpperCase()
            const personalColor = (member as IdolMember & { personal_color?: string | null }).personal_color

            return (
              <div
                key={member.id}
                className="group relative flex flex-col rounded-2xl border border-border bg-card overflow-hidden shadow-sm transition-shadow hover:shadow-md"
              >
                {/* Personal color accent bar */}
                {personalColor && (
                  <div
                    className="h-1 w-full shrink-0"
                    style={{ backgroundColor: personalColor }}
                  />
                )}

                {/* Clickable area → detail page */}
                <div
                  className="flex flex-col cursor-pointer"
                  onClick={() => navigate(`/members/${member.id}`)}
                >
                  {/* Photo */}
                  <div className="relative aspect-[3/4] w-full overflow-hidden bg-muted">
                    {member.photo_url ? (
                      <img
                        src={member.photo_url}
                        alt={displayName}
                        className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center text-4xl font-bold text-muted-foreground/40 select-none">
                        {initial}
                      </div>
                    )}

                    {/* Status badge overlay */}
                    <div className="absolute top-2 right-2">
                      <span
                        className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold shadow ${
                          member.is_active
                            ? 'bg-green-500 text-white'
                            : 'bg-zinc-400 text-white'
                        }`}
                      >
                        {member.is_active ? 'Aktif' : 'Nonaktif'}
                      </span>
                    </div>
                  </div>

                  {/* Info */}
                  <div className="flex flex-col gap-0.5 px-3 pt-3 pb-1">
                    <p className="truncate text-sm font-semibold leading-tight">
                      {displayName}
                    </p>
                    {member.stage_name && member.stage_name !== member.name && (
                      <p className="truncate text-[11px] text-muted-foreground">
                        {member.name}
                      </p>
                    )}
                    {member.position && (
                      <p className="truncate text-[11px] text-muted-foreground">
                        {member.position}
                      </p>
                    )}
                  </div>
                </div>

                {/* Actions — stopPropagation so card click doesn't fire */}
                <div
                  className="flex items-center justify-end gap-0.5 px-2 pb-2 pt-1"
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    onClick={() => navigate(`/members/${member.id}/cheki`)}
                    className="rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
                    title="Riwayat Cheki"
                  >
                    <History className="h-3.5 w-3.5" />
                  </button>
                  <button
                    onClick={() =>
                      updateMember.mutate({
                        id: member.id,
                        is_active: !member.is_active,
                      })
                    }
                    className="rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
                    title={member.is_active ? 'Nonaktifkan' : 'Aktifkan'}
                  >
                    {member.is_active ? (
                      <EyeOff className="h-3.5 w-3.5" />
                    ) : (
                      <Eye className="h-3.5 w-3.5" />
                    )}
                  </button>
                  <button
                    onClick={() => navigate(`/members/${member.id}/edit`)}
                    className="rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
                    title="Edit member"
                  >
                    <Edit className="h-3.5 w-3.5" />
                  </button>
                  <button
                    onClick={() => setDeleteTarget(member)}
                    className="rounded-md p-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors"
                    title="Hapus member"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {deleteTarget && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setDeleteTarget(null) }}
        >
          <div className="w-full max-w-sm rounded-xl border border-border bg-card p-6 shadow-xl space-y-4">
            <h2 className="font-semibold">Hapus Member?</h2>
            <p className="text-sm text-muted-foreground">
              <strong className="text-foreground">
                {deleteTarget.stage_name ?? deleteTarget.name}
              </strong>{' '}
              akan dihapus secara permanen dan tidak bisa dikembalikan.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setDeleteTarget(null)}
                className="rounded-md border border-border px-4 py-2 text-sm hover:bg-accent"
              >
                Batal
              </button>
              <button
                onClick={() =>
                  deleteMember.mutate(deleteTarget.id, {
                    onSuccess: () => setDeleteTarget(null),
                  })
                }
                disabled={deleteMember.isPending}
                className="rounded-md bg-destructive px-4 py-2 text-sm font-medium text-white hover:bg-destructive/90 disabled:opacity-50"
              >
                {deleteMember.isPending ? 'Menghapus...' : 'Hapus'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </FeatureGate>
  )
}
